import React from "react";
import clsx from "clsx";
import * as Icon from "react-feather";

import WComponent from "../common/WComponent";
import ChoicePrompt from "../clients/Tools/AFSTool/tabs/review/checklist/prompts/ChoicePrompt";
import { getStepStatusConfig, getStepStatusClass } from "../clients/Tools/AFSTool/tabs/review/checklist/tools/StepStatusIcon";
import PlaybookReviewService from "../../services/PlaybookReviewService";
import PlaybookRuleService from "../../services/PlaybookRuleService";
import UtilityService from "../../services/UtilityService";
import PlaybookDecisionForm from "./PlaybookDecisionForm";

import "../clients/Tools/AFSTool/afs-walkthrough.css";
import "./agent-finding-steps.css";

class AgentFindingSteps extends WComponent {
	constructor(props) {
		super(props);
		this.state = {
			showCompleted: !!props.isProposal,
			isChangingDecision: false,
			formDecision: null,
			isMakingRule: false,
			ruleMade: false,
			ruleError: null,
		};
	}

	onMakeRule = async () => {
		const { subject } = this.props;
		await this.update({ isMakingRule: true, ruleError: null });
		try {
			await PlaybookRuleService.createRule({ fromDecisionId: subject.decision.id, scopeKind: "client" });
			await this.update({ isMakingRule: false, ruleMade: true });
		} catch (error) {
			await this.update({ isMakingRule: false, ruleError: UtilityService.describeRequestError({ error, fallback: "The rule was not set." }) });
		}
	};

	choicesForSubject() {
		const { subject, isProposal, review } = this.props;
		const choices = isProposal ? null : PlaybookReviewService.choicesFor({ subject, review });
		return choices;
	}

	renderMakeRule() {
		const { subject, review } = this.props;
		const { canDecide } = review.viewer;
		const { isMakingRule, ruleMade, ruleError } = this.state;
		const isLiveRejection = subject.decision && subject.decision.decision === "reject";
		const hasNote = isLiveRejection && !!String(subject.decision.reason || "").trim();
		if (!hasNote || !canDecide) return null;
		if (ruleMade) return <span className="agent-finding-steps__rule-made">Rule set for this client — later runs are handed it.</span>;
		return (
			<span className="agent-finding-steps__make-rule">
				<span
					role="button"
					tabIndex={0}
					className="afs-review__exec-change-link"
					onClick={isMakingRule ? undefined : this.onMakeRule}
					onKeyDown={event => this.onKeyActivate({ event, activate: this.onMakeRule })}
				>
					{isMakingRule ? "setting the rule…" : "make this a rule for this client"}
				</span>
				{ruleError && <span className="agent-finding-steps__rule-error">{ruleError}</span>}
			</span>
		);
	}

	onChoose = optionId => {
		const { subject } = this.props;
		const choices = this.choicesForSubject();
		const isAccepting = optionId === "accept";
		const needsAnAnswer = isAccepting && PlaybookReviewService.needsAnswerToAccept({ subject, choices });
		if (isAccepting && !needsAnAnswer) {
			this.saveDecision({ decision: "accept" });
			return;
		}
		this.update({ formDecision: optionId });
	};

	saveDecision = async ({ decision, reasonCategory, reason, chosenValue }) => {
		const isSaved = await this.props.onDecide({ decision, reasonCategory, reason, chosenValue });
		if (isSaved) await this.update({ isChangingDecision: false, formDecision: null });
	};

	onSaveForm = ({ reasonCategory, reason, chosenValue }) => {
		const { formDecision } = this.state;
		this.saveDecision({ decision: formDecision, reasonCategory, reason, chosenValue });
	};

	onToggleCompleted = () => {
		this.update({ showCompleted: !this.state.showCompleted });
	};

	onStartChangingDecision = () => {
		this.update({ isChangingDecision: true, showCompleted: false });
	};

	onKeyActivate = ({ event, activate }) => {
		if (event.key !== "Enter" && event.key !== " ") return;
		event.preventDefault();
		activate();
	};

	renderDecisionForm() {
		const { isBusy } = this.props;
		const { formDecision } = this.state;
		return (
			<PlaybookDecisionForm
				isRejection={formDecision === "reject"}
				choices={this.choicesForSubject()}
				isBusy={isBusy}
				onSave={this.onSaveForm}
				onBack={() => this.update({ formDecision: null })}
			/>
		);
	}

	renderLastMonth() {
		const { subject, review, isBusy, onConfirmStillApplies } = this.props;
		const { canDecide } = review.viewer;
		const { lastMonth } = subject;
		if (!lastMonth) return null;
		const { isRejection, decisionLabel, decidedBy, reasonText, amountNote } = PlaybookReviewService.describeLastMonthDecision({ lastMonth });
		const canCarryForward = lastMonth.canConfirmStillApplies && canDecide && !subject.decision;

		return (
			<div className="agent-finding-steps__last-month">
				<Icon.RotateCcw size={12} />
				<span>
					{decisionLabel} last month by {decidedBy} ({amountNote}){isRejection ? ` — ${reasonText}` : " — and it is still here."}
				</span>
				{canCarryForward && (
					<button type="button" className="afs-review__exec-prompt-btn" onClick={onConfirmStillApplies} disabled={isBusy}>
						Still applies
					</button>
				)}
			</div>
		);
	}

	renderDecidePrompt() {
		const { subject, isProposal, review } = this.props;
		const { formDecision } = this.state;
		const { canDecide, canDecideJournals, whyNotDecide } = review.viewer;
		const mayDecideHere = isProposal ? canDecideJournals : canDecide;
		if (!mayDecideHere) {
			const booking = isProposal ? PlaybookReviewService.describeJournalBooking({ proposal: subject, viewer: review.viewer }) : null;
			const acceptsByBooking = !!booking && booking.isAcceptedByBooking;
			const waitingText = acceptsByBooking ? "Accept it by booking it below." : whyNotDecide;
			return <span className="agent-finding-steps__waiting">{waitingText}</span>;
		}
		if (formDecision) return this.renderDecisionForm();
		return <ChoicePrompt question="Is the agent right?" options={PlaybookReviewService.decisionChoices()} onSelect={this.onChoose} />;
	}

	renderHeroStep(step) {
		const config = getStepStatusConfig(step.status);
		const StepIcon = config.icon;
		const isDecideStep = step.id === "decide";
		const isAskingForDecision = isDecideStep && step.status === "waiting_for_human";

		return (
			<div className={clsx("afs-review__exec-step", getStepStatusClass(step.status))}>
				<StepIcon size={16} color={config.color} className="afs-review__exec-step-icon" />
				<div className="afs-review__exec-step-content">
					<span className="afs-review__exec-step-label">{step.label}</span>
					{step.detail && <span className="afs-review__exec-step-detail">{step.detail}</span>}
					{isDecideStep && this.renderLastMonth()}
					{isAskingForDecision && this.renderDecidePrompt()}
				</div>
			</div>
		);
	}

	renderCompleted(completedSteps) {
		const { subject, isProposal, review } = this.props;
		const { canDecide, canDecideJournals } = review.viewer;
		const mayDecideHere = isProposal ? canDecideJournals : canDecide;
		const booking = isProposal ? PlaybookReviewService.describeJournalBooking({ proposal: subject, viewer: review.viewer }) : null;
		const isDecisionChangeable = !booking || booking.isDecisionChangeable;
		const mayChangeDecision = mayDecideHere && isDecisionChangeable;
		return (
			<div className="afs-review__exec-completed-steps">
				{completedSteps.map(step => (
					<div key={step.id} className="afs-review__exec-completed-item">
						<Icon.Check size={12} className="agent-finding-steps__done-icon" />
						<span>{step.label}</span>
						{step.detail && <span className="afs-review__exec-step-detail"> — {step.detail}</span>}
						{step.id === "decide" && mayChangeDecision && (
							<span
								role="button"
								tabIndex={0}
								className="afs-review__exec-change-link"
								onClick={this.onStartChangingDecision}
								onKeyDown={event => this.onKeyActivate({ event, activate: this.onStartChangingDecision })}
							>
								change
							</span>
						)}
						{step.id === "decide" && this.renderMakeRule()}
					</div>
				))}
			</div>
		);
	}

	render() {
		const { subject, isProposal, review } = this.props;
		const { showCompleted, isChangingDecision } = this.state;
		if (!PlaybookReviewService.isInDecisionFlow({ review })) return null;
		const steps = PlaybookReviewService.describeFindingSteps({ subject, isProposal, review });
		const applicable = steps.filter(step => step.status !== "not_applicable");
		const decideStep = steps.find(step => step.id === "decide");
		const booking = isProposal ? PlaybookReviewService.describeJournalBooking({ proposal: subject, viewer: review.viewer }) : null;
		const isChangingThisDecision = isChangingDecision && (!booking || booking.isDecisionChangeable);
		const completedSteps = applicable.filter(step => step.status === "completed" && !(isChangingThisDecision && step.id === "decide"));
		const heroStep = isChangingThisDecision
			? { ...decideStep, label: "Change the decision", status: "waiting_for_human", detail: null }
			: applicable.find(step => step.status !== "completed");
		const isAllDone = !heroStep;

		return (
			<div className="afs-review__exec-steps">
				{completedSteps.length > 0 && (
					<div
						role="button"
						tabIndex={0}
						aria-expanded={showCompleted}
						className={clsx("afs-review__exec-summary", isAllDone && "afs-review__exec-summary--done")}
						onClick={this.onToggleCompleted}
						onKeyDown={event => this.onKeyActivate({ event, activate: this.onToggleCompleted })}
					>
						<Icon.CheckCircle size={14} className="agent-finding-steps__done-icon" />
						<span>
							{isAllDone ? `All ${completedSteps.length} steps completed` : `${completedSteps.length} of ${applicable.length} steps completed`}
						</span>
						<span className="afs-review__exec-summary-toggle">{showCompleted ? "hide" : "show"}</span>
					</div>
				)}
				{showCompleted && this.renderCompleted(completedSteps)}
				{heroStep && this.renderHeroStep(heroStep)}
				{isChangingThisDecision && (
					<button
						type="button"
						className="tk-text-action tk-text-action--muted agent-finding-steps__keep"
						onClick={() => this.update({ isChangingDecision: false, formDecision: null })}
					>
						Keep the decision as it is
					</button>
				)}
			</div>
		);
	}
}

export default AgentFindingSteps;
